import { ReactElement, useEffect, useState } from 'react';
import { Modal, TextInput, TextArea } from 'carbon-components-react';
import { segmentsTracking } from '../lib/tracker';
import { FormattedMessage } from 'react-intl';
import '../style/FeedbackModal.scss';
import '../style/common.scss';
import { getMessage } from '../hooks/widgets/useLang';
import { ModalMessage } from './modalMessage';
import { responseProperties } from '../hooks/widgets/fetchData';
import emailNotification from './emailNotification';
import { useReadCOS } from './readCOS';
import { emailVariablesProperties } from './emailNotification';

export interface FeedbackModalProperties {
  pageTitle: string;
  object: string;
  messages: any;
  productTitle: string;
  pageUrl: string;
  emailJson: string;
  thumbsUp: boolean;
  showFeedbackModal: boolean;
  setShowFeedbackModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const FeedbackModal = ({
  pageTitle,
  object,
  messages,
  productTitle,
  pageUrl,
  emailJson,
  thumbsUp,
  showFeedbackModal,
  setShowFeedbackModal,
}: FeedbackModalProperties): ReactElement => {
  const MAX_FEEDBACK_CHAR = 1500;
  const MIN_FEEDBACK_CHAR = 4;
  const [textArea, setTextArea] = useState('');
  const [email, setEmail] = useState('');
  const [invalidMax, setInvalidMax] = useState(false);
  const [invalidMin, setInvalidMin] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [counter, setCounter] = useState(0);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [textResponse, setTextResponse] = useState(['']);
  const message203 = getMessage(messages.feedback203);
  const message200 = getMessage(messages.feedback200);
  const message500 = getMessage(messages.feedback500);
  const [response, setResponse] = useState({} as responseProperties);
  const [localFeedback, setLocalFeedback] = useState('');

  const emailVariables = {} as emailVariablesProperties;
  const [vars, setVars] = useState(emailVariables);

  /**
   *
   */
  useReadCOS(localFeedback, setVars, emailJson);
  /**
   *
   */
  emailNotification(localFeedback, setResponse, vars);

  useEffect(() => {
    if (response) {
      if (localFeedback.length !== 0) {
        switch (response.status) {
          case 203: {
            setTextResponse([message203]);
            setShowResponseModal(true);
            break;
          }
          case 200: {
            setTextResponse([message200]);
            setShowResponseModal(true);
            break;
          }
          default: {
            setTextResponse([message500]);
            setShowResponseModal(true);
          }
        }
      }
    }
  }, [response, vars]);

  const render = (
    <div className="FeedbackModal">
      <div className="bx--row">
        <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16 mar1">
          <Modal
            open={showFeedbackModal}
            modalHeading={`${getMessage(messages.feedbackHeaderText)}`}
            primaryButtonText={
              <FormattedMessage {...messages.feedbackSubmit} />
            }
            secondaryButtonText={
              <FormattedMessage {...messages.feedbackCancel} />
            }
            onRequestClose={() => {
              segmentsTracking('CTA Clicked', {
                productTitle,
                pageTitle,
                object,
                pageUrl,
                resultValue: 'CTA clicked',
                CTA: 'Feedback closed on the corner',
                location: 'Estimator',
                action: `Positive response marked${thumbsUp}`,
                field: '',
              });
              setInvalidMax(false);
              setInvalidMin(false);
              setShowFeedbackModal(false);
            }}
            onRequestSubmit={() => {
              segmentsTracking('CTA Clicked', {
                productTitle,
                pageTitle,
                object,
                pageUrl,
                resultValue: 'CTA clicked',
                CTA: 'Feedback submitted',
                location: 'Estimator',
                action: `Positive response marked${thumbsUp}`,
                field: '',
              });
              if (textArea.length == 0) {
                setInvalidMin(true);
                return;
              }
              let textToSend = '';
              if (!invalidEmail && email.length !== 0) {
                textToSend = `Email from: ${email} <br>`;
              }
              textToSend += `Thumbs Up: ${thumbsUp} <br>`;
              setShowFeedbackModal(false);
              setLocalFeedback(`${textToSend}Message: ${textArea}`);
            }}
            onSecondarySubmit={() => {
              setShowFeedbackModal(false);
              segmentsTracking('CTA Clicked', {
                productTitle,
                pageTitle,
                object,
                pageUrl,
                resultValue: 'CTA clicked',
                CTA: 'Feedback canceled',
                location: 'Estimator',
                action: `Positive response marked${thumbsUp}`,
                field: '',
              });
              setInvalidMax(false);
              setInvalidMin(false);
              setShowFeedbackModal(false);
            }}
          >
            <p className="mar5">
              <span className="slide_text">
                <FormattedMessage {...messages.feddbackPhraseText} />
              </span>
              <br />
            </p>
            <TextArea
              labelText={
                <span className="slide_text">
                  <FormattedMessage {...messages.feedbackComment} />
                </span>
              }
              id={'feedBackId'}
              rows={4}
              helperText={` ${counter} - ${MAX_FEEDBACK_CHAR} ${getMessage(
                messages.feedbackCharacters,
              )}`}
              placeholder={`${getMessage(messages.feedbackCommentHolder)}`}
              maxCount={MAX_FEEDBACK_CHAR}
              invalid={invalidMax || invalidMin}
              invalidText={
                invalidMax ? (
                  <span className="slide_text">
                    <FormattedMessage {...messages.feedbackInvalidMaximum} />
                    {` ${MAX_FEEDBACK_CHAR}`}
                  </span>
                ) : invalidMin ? (
                  <span className="slide_text">
                    <FormattedMessage {...messages.feedbackInvalidMinimum} />
                    {` ${MIN_FEEDBACK_CHAR}`}
                  </span>
                ) : null
              }
              onChange={(e) => {
                setCounter(e.target.value.length);
                if (counter > MAX_FEEDBACK_CHAR) {
                  setInvalidMax(true);
                } else {
                  setInvalidMax(false);
                  setInvalidMin(false);
                  setTextArea(e.target.value);
                }
              }}
            />
            <br />
            <TextInput
              labelText={
                <span className="slide_text">
                  <FormattedMessage {...messages.feedbackEmail} />
                </span>
              }
              id="feedbackInputId"
              placeholder={`${getMessage(messages.feedbackEmailHolder)}`}
              invalid={invalidEmail}
              invalidText={
                <FormattedMessage {...messages.feedbackInvalidEmail} />
              }
              onChange={({ target }) => {
                const value = target.value;
                const regex = new RegExp(
                  '^[a-zA-Z_0-9.]+[@]+[a-zA-Z_0-9]+[.]+[a-zA-z.]+$',
                );
                const invalid = !regex.test(value) && value.length > 0;
                setInvalidEmail(invalid);
                if (!invalid) setEmail(target.value);
              }}
            />
          </Modal>
          <ModalMessage
            setShowResponseModal={setShowResponseModal}
            showResponseModal={showResponseModal}
            textResponse={textResponse}
            messages={messages}
            modalTitle={`${getMessage(messages.modalResponseText)}`}
          ></ModalMessage>
        </div>
      </div>
    </div>
  );
  return render;
};

export default FeedbackModal;
